import type { ContextProposal, FeedbackItem, QualityWeek } from '@lastrouter/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  Field,
  NativeSelect,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@lastrouter/ui';
import { RefreshCw } from '@lastrouter/ui';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation, useApiQuery } from '../api/hooks.js';
import { useSession } from '../app/session.js';
import { AnswerRateChart, MAX_SERIES, type Series } from '../components/answer-rate-chart.js';
import { EmptyState, QueryState, PageHeader, StatusBadge } from '../components/common.js';
import { Markdown } from '../components/markdown.js';
import { formatDateTime, formatNumber, formatPercent } from '../lib/format.js';

export type QualityTab = 'weekly' | 'feedback' | 'proposals' | 'candidates' | 'reports';

function WeeklyTable({ weeks, names }: { weeks: QualityWeek[]; names: Map<string, string> }) {
  const { t } = useTranslation();
  return (
    <Table>
      <TableCaption>{t('quality.chartTitle')}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>{t('quality.subject')}</TableHead>
          <TableHead>{t('quality.week')}</TableHead>
          <TableHead className="text-right">{t('quality.questions')}</TableHead>
          <TableHead className="text-right">{t('quality.answered')}</TableHead>
          <TableHead className="text-right">{t('quality.clarifications')}</TableHead>
          <TableHead className="text-right">{t('quality.noAnswer')}</TableHead>
          <TableHead className="text-right">{t('quality.errors')}</TableHead>
          <TableHead className="text-right">{t('quality.negative')}</TableHead>
          <TableHead className="text-right">{t('quality.rate')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {weeks.map((w) => (
          <TableRow key={`${w.subject_id}-${w.week}`}>
            <TableCell>{w.subject_id ? (names.get(w.subject_id) ?? w.subject_id) : t('quality.noSubject')}</TableCell>
            <TableCell className="tabular">{w.week}</TableCell>
            <TableCell className="tabular text-right">{formatNumber(w.questions)}</TableCell>
            <TableCell className="tabular text-right">{formatNumber(w.answered)}</TableCell>
            <TableCell className="tabular text-right">{formatNumber(w.clarifications)}</TableCell>
            <TableCell className="tabular text-right">{formatNumber(w.no_answer)}</TableCell>
            <TableCell className="tabular text-right">{formatNumber(w.errors)}</TableCell>
            <TableCell className="tabular text-right">{formatNumber(w.negative_feedback)}</TableCell>
            <TableCell className="tabular text-right">
              {formatPercent(w.answer_rate)}
              {w.needs_review ? (
                <span className="ml-2">
                  <StatusBadge tone="warning">{t('quality.review')}</StatusBadge>
                </span>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** subjectId: só um app (aba Qualidade da página do app). */
function WeeklyView({ subjectId }: { subjectId?: string }) {
  const { t } = useTranslation();
  const raw = useApiQuery('qualityWeekly', { query: { weeks: 8 } });
  const q = useMemo(() => (subjectId && raw.data ? { ...raw, data: raw.data.filter((w) => w.subject_id === subjectId) } : raw), [raw, subjectId]) as typeof raw;
  const catalog = useApiQuery('catalog');
  const [asTable, setAsTable] = useState(false);
  const names = useMemo(() => new Map((catalog.data?.snapshot?.subjects ?? []).map((s) => [s.id, s.nome])), [catalog.data]);
  const all: Series[] = useMemo(() => {
    const ids = [...new Set((q.data ?? []).map((w) => w.subject_id ?? ''))];
    return ids.map((id) => ({ id, label: id ? (names.get(id) ?? id) : t('quality.noSubject') }));
  }, [q.data, names, t]);
  const [selected, setSelected] = useState<string[] | null>(null);
  const chosen = all.filter((s) => (selected ?? all.slice(0, MAX_SERIES).map((x) => x.id)).includes(s.id));

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="flex max-w-2xl flex-col gap-1">
          <CardTitle>{t('quality.chartTitle')}</CardTitle>
          <CardDescription>{t('quality.chartHelp')}</CardDescription>
        </div>
        <Button size="sm" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>
          {asTable ? t('quality.showChart') : t('quality.showTable')}
        </Button>
      </CardHeader>
      <CardContent>
        <QueryState query={q}>
          {(weeks) =>
            weeks.length === 0 ? (
              <EmptyState>{t('quality.noData')}</EmptyState>
            ) : asTable ? (
              <WeeklyTable weeks={weeks} names={names} />
            ) : (
              <div className="flex flex-col gap-3">
                {all.length > MAX_SERIES ? (
                  <fieldset className="flex flex-wrap gap-4">
                    <legend className="sr-only">{t('quality.subject')}</legend>
                    {all.map((s) => {
                      const on = chosen.some((c) => c.id === s.id);
                      return (
                        <label key={s.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={on}
                            disabled={!on && chosen.length >= MAX_SERIES}
                            onChange={(e) => setSelected((e.target.checked ? [...chosen.map((c) => c.id), s.id] : chosen.map((c) => c.id).filter((x) => x !== s.id)))}
                          />
                          {s.label}
                        </label>
                      );
                    })}
                  </fieldset>
                ) : null}
                <AnswerRateChart weeks={weeks} series={chosen} />
              </div>
            )
          }
        </QueryState>
      </CardContent>
    </Card>
  );
}

const FEEDBACK_TONE = { aberto: 'warning', em_revisao: 'accent', resolvido: 'success', descartado: 'neutral' } as const;

function FeedbackDialog({ item, onClose }: { item: FeedbackItem; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useApiMutation('updateFeedback', ['feedback', 'overview']);
  const [status, setStatus] = useState(item.status);
  const [resolution, setResolution] = useState(item.resolution ?? '');
  function submit(e: FormEvent) {
    e.preventDefault();
    m.mutate(
      { params: { id: item.id }, body: { status, resolution: resolution.trim() || null } },
      {
        onSuccess: () => {
          toast.success(t('quality.feedback.saved'));
          onClose();
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('quality.feedback.change')} description={item.comment ?? t(`quality.feedback.kinds.${item.kind}`)} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field id="feedback-estado" label={t('quality.feedback.state')}>
            <NativeSelect value={status} onChange={(e) => setStatus(e.target.value as FeedbackItem['status'])}>
              {(['aberto', 'em_revisao', 'resolvido', 'descartado'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`quality.feedback.states.${s}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="feedback-resolucao" label={`${t('quality.feedback.resolution')} (${t('common.optional')})`}>
            <Textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} maxLength={2000} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={m.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackView({ subjectId }: { subjectId?: string }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<FeedbackItem['status'] | ''>('');
  const q = useApiQuery('feedback', { query: { ...(status ? { status } : {}), ...(subjectId ? { subject_id: subjectId } : {}) } });
  const [editing, setEditing] = useState<FeedbackItem | null>(null);
  return (
    <div className="flex flex-col gap-3">
      <Field id="filtro-feedback" label={t('quality.feedback.state')} className="max-w-xs">
        <NativeSelect value={status} onChange={(e) => setStatus(e.target.value as FeedbackItem['status'] | '')}>
          <option value="">{t('common.all')}</option>
          {(['aberto', 'em_revisao', 'resolvido', 'descartado'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`quality.feedback.states.${s}`)}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <QueryState query={q}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState>{t('quality.feedback.empty')}</EmptyState>
          ) : (
            <Table>
              <TableCaption>{t('quality.tabFeedback')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('quality.feedback.when')}</TableHead>
                  <TableHead>{t('quality.feedback.kind')}</TableHead>
                  <TableHead>{t('quality.feedback.comment')}</TableHead>
                  <TableHead>{t('quality.feedback.query')}</TableHead>
                  <TableHead>{t('quality.feedback.routed')}</TableHead>
                  <TableHead>{t('quality.feedback.state')}</TableHead>
                  <TableHead>
                    <span className="sr-only">{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="tabular whitespace-nowrap">{formatDateTime(f.ts)}</TableCell>
                    <TableCell>{t(`quality.feedback.kinds.${f.kind}`)}</TableCell>
                    <TableCell>
                      {f.comment ?? '-'}
                      {f.resolution ? <p className="text-[length:var(--lr-text-xs)] text-fg-secondary">{f.resolution}</p> : null}
                    </TableCell>
                    <TableCell className="font-mono text-[length:var(--lr-text-xs)]">{f.query_id}</TableCell>
                    <TableCell className="text-[length:var(--lr-text-sm)]">{f.routed_to.join(', ') || '-'}</TableCell>
                    <TableCell>
                      <StatusBadge tone={FEEDBACK_TONE[f.status]}>{t(`quality.feedback.states.${f.status}`)}</StatusBadge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setEditing(f)}>
                        {t('quality.feedback.change')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </QueryState>
      {editing ? <FeedbackDialog item={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

const PROPOSAL_TONE = { proposta: 'warning', aceita: 'success', recusada: 'neutral' } as const;

function ProposalCard({ p }: { p: ContextProposal }) {
  const { t } = useTranslation();
  const m = useApiMutation('decideProposal', ['proposals']);
  const decide = (decision: 'aceita' | 'recusada') =>
    m.mutate({ params: { id: p.id }, body: { decision } }, { onSuccess: () => decision === 'aceita' && toast.info(t('quality.proposals.acceptHelp')), onError: (err) => toast.error(errorMessage(t, err)) });
  return (
    <Card>
      <CardContent className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="accent">{t(`quality.proposals.kinds.${p.kind}`)}</Badge>
          <span className="font-mono text-[length:var(--lr-text-xs)] text-fg-muted">{p.subject_id}</span>
          <span className="font-mono text-[length:var(--lr-text-xs)] text-fg-muted">
            {p.target.type} {p.target.id}
          </span>
          <StatusBadge tone={PROPOSAL_TONE[p.status]}>{t(`quality.proposals.states.${p.status}`)}</StatusBadge>
        </div>
        <p>{p.suggestion}</p>
        {p.evidence.questions.length ? (
          <details>
            <summary className="cursor-pointer text-[length:var(--lr-text-sm)] text-fg-secondary">
              {t('quality.proposals.evidence')} ({p.evidence.questions.length})
            </summary>
            <ul className="mt-1 list-disc pl-5 text-[length:var(--lr-text-sm)]">
              {p.evidence.questions.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
          </details>
        ) : null}
        {p.status === 'proposta' ? (
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={m.isPending} onClick={() => decide('aceita')}>
              {t('quality.proposals.accept')}
            </Button>
            <Button size="sm" disabled={m.isPending} onClick={() => decide('recusada')}>
              {t('quality.proposals.refuse')}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ProposalsView({ subjectId }: { subjectId?: string }) {
  const { t } = useTranslation();
  const s = useSession().data;
  const raw = useApiQuery('proposals');
  const q = useMemo(() => (subjectId && raw.data ? { ...raw, data: raw.data.filter((p) => p.subject_id === subjectId) } : raw), [raw, subjectId]) as typeof raw;
  const refresh = useApiMutation('refreshProposals', ['proposals']);
  return (
    <div className="flex flex-col gap-3">
      {s?.is_admin ? (
        <div>
          <Button
            onClick={() => refresh.mutate({}, { onSuccess: (r) => toast.success(t('quality.proposals.generated', { count: r.length })), onError: (err) => toast.error(errorMessage(t, err)) })}
            disabled={refresh.isPending}
          >
            <RefreshCw />
            {t('quality.proposals.generate')}
          </Button>
        </div>
      ) : null}
      <QueryState query={q}>{(items) => (items.length === 0 ? <EmptyState>{t('quality.proposals.empty')}</EmptyState> : items.map((p) => <ProposalCard key={p.id} p={p} />))}</QueryState>
    </div>
  );
}

function CandidatesView() {
  const { t } = useTranslation();
  const q = useApiQuery('suiteCandidates');
  return (
    <div className="flex flex-col gap-3">
      <p className="text-fg-secondary">{t('quality.candidates.help')}</p>
      <QueryState query={q}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState>{t('quality.candidates.empty')}</EmptyState>
          ) : (
            <Table>
              <TableCaption>{t('quality.tabCandidates')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('quality.candidates.question')}</TableHead>
                  <TableHead>{t('quality.candidates.reason')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.question}>
                    <TableCell>{c.question}</TableCell>
                    <TableCell>{c.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </QueryState>
    </div>
  );
}

function ReportDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const q = useApiQuery('evalReport', { params: { id } });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={q.data?.label ?? t('quality.reports.open')} closeLabel={t('common.close')} className="w-[min(900px,calc(100vw-32px))]">
        <QueryState query={q}>{(r) => <Markdown source={r.markdown} />}</QueryState>
      </DialogContent>
    </Dialog>
  );
}

function ReportsView() {
  const { t } = useTranslation();
  const q = useApiQuery('evalReports');
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <QueryState query={q}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState>{t('quality.reports.empty')}</EmptyState>
          ) : (
            <Table>
              <TableCaption>{t('quality.tabReports')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('quality.reports.label')}</TableHead>
                  <TableHead>{t('quality.reports.when')}</TableHead>
                  <TableHead className="text-right">{t('quality.reports.answerRate')}</TableHead>
                  <TableHead className="text-right">{t('quality.reports.accuracy')}</TableHead>
                  <TableHead className="text-right">{t('quality.reports.certifiedWrong')}</TableHead>
                  <TableHead className="text-right">{t('quality.reports.deterministic')}</TableHead>
                  <TableHead>
                    <span className="sr-only">{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell className="tabular whitespace-nowrap">{formatDateTime(r.created_at)}</TableCell>
                    <TableCell className="tabular text-right">{formatPercent(r.answer_rate)}</TableCell>
                    <TableCell className="tabular text-right">{formatPercent(r.accuracy)}</TableCell>
                    <TableCell className="tabular text-right">
                      {r.certified_wrong > 0 ? (
                        <StatusBadge tone="danger">{r.certified_wrong}</StatusBadge>
                      ) : (
                        r.certified_wrong
                      )}
                    </TableCell>
                    <TableCell className="tabular text-right">{r.deterministic ? `${r.deterministic.passed}/${r.deterministic.total}` : '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setOpen(r.id)}>
                        {t('quality.reports.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </QueryState>
      {open ? <ReportDialog id={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/** Qualidade de um app: respostas por semana, retorno das pessoas e correções propostas, só deste assunto. */
export function AppQuality({ subjectId }: { subjectId: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <WeeklyView subjectId={subjectId} />
      <section className="flex flex-col gap-3" aria-labelledby="qualidade-retorno">
        <h2 id="qualidade-retorno" className="text-[length:var(--lr-text-lg)] font-semibold">
          {t('quality.tabFeedback')}
        </h2>
        <FeedbackView subjectId={subjectId} />
      </section>
      <section className="flex flex-col gap-3" aria-labelledby="qualidade-propostas">
        <h2 id="qualidade-propostas" className="text-[length:var(--lr-text-lg)] font-semibold">
          {t('quality.tabProposals')}
        </h2>
        <ProposalsView subjectId={subjectId} />
      </section>
    </div>
  );
}

export function QualityPage({ tab, onTab }: { tab: QualityTab; onTab: (t: QualityTab) => void }) {
  const { t } = useTranslation();
  // Relatórios da suíte cobrem todos os assuntos: só o admin vê.
  const isAdmin = Boolean(useSession().data?.is_admin);
  return (
    <>
      <PageHeader title={t('quality.title')} subtitle={t('quality.subtitle')} />
      <Tabs value={tab} onValueChange={(v) => onTab(v as QualityTab)}>
        <TabsList aria-label={t('quality.title')} className="overflow-x-auto">
          <TabsTrigger value="weekly">{t('quality.tabWeekly')}</TabsTrigger>
          <TabsTrigger value="feedback">{t('quality.tabFeedback')}</TabsTrigger>
          <TabsTrigger value="proposals">{t('quality.tabProposals')}</TabsTrigger>
          <TabsTrigger value="candidates">{t('quality.tabCandidates')}</TabsTrigger>
          {isAdmin ? <TabsTrigger value="reports">{t('quality.tabReports')}</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="weekly">
          <WeeklyView />
        </TabsContent>
        <TabsContent value="feedback">
          <FeedbackView />
        </TabsContent>
        <TabsContent value="proposals">
          <ProposalsView />
        </TabsContent>
        <TabsContent value="candidates">
          <CandidatesView />
        </TabsContent>
        {isAdmin ? (
          <TabsContent value="reports">
            <ReportsView />
          </TabsContent>
        ) : null}
      </Tabs>
    </>
  );
}
